/**
 * Parsing helpers for the concrete-slot CSV import response.
 *
 * The import endpoint answers 409 whenever ANY row failed — including the
 * partial case where other rows committed successfully. `req` throws for that
 * status, so the "3 of 4 slots imported" outcome arrives on the CATCH branch
 * and the caller must still refresh.
 */

export interface ImportRowError {
  [key: string]: string
}

export interface ImportCounts {
  successCount: number
  failedCount: number
  errors: ImportRowError[]
}

interface RawImportEnvelope {
  success?: { count?: number }
  failed?: { count?: number; errors?: ImportRowError[] }
}

const readCounts = (envelope: RawImportEnvelope | null): ImportCounts => ({
  successCount: envelope?.success?.count || 0,
  failedCount: envelope?.failed?.count || 0,
  errors: Array.isArray(envelope?.failed?.errors)
    ? (envelope?.failed?.errors as ImportRowError[])
    : [],
})

/**
 * Counts from a RESOLVED import response (2xx). `req` returns the parsed body;
 * some endpoints nest it under `data`, so accept either shape.
 */
export const parseImportResponse = (response: unknown): ImportCounts => {
  if (!response || typeof response !== 'object') {
    return readCounts(null)
  }
  const body = response as { data?: unknown }
  const envelope =
    body.data && typeof body.data === 'object' ? body.data : response
  return readCounts(envelope as RawImportEnvelope)
}

/**
 * Counts from a REJECTED import — but only for the partial-success 409.
 * Returns null for anything else (network failure, 422, 409 with nothing
 * committed) so the caller falls through to its generic error branch.
 *
 * Two things this must not do:
 *  - guard on `err instanceof Error`. `throwOnHTTPError` throws the PARSED JSON
 *    body with flags bolted on, so a 409 is a plain object and that check is
 *    false.
 *  - look under `.data` / `.bodyData`. Only 400/422 go through
 *    `normalizeApiError`; a 409's counts sit at the top level.
 */
export const parsePartialImportError = (err: unknown): ImportCounts | null => {
  if (!err || typeof err !== 'object') {
    return null
  }

  const apiError = err as {
    isApiError?: boolean
    status?: number
    failed?: { count?: number; errors?: ImportRowError[] }
    success?: { count?: number }
  }

  if (apiError.isApiError !== true || apiError.status !== 409) {
    return null
  }

  const counts = readCounts(apiError)
  if (counts.failedCount <= 0) {
    return null
  }

  return counts
}
